import { authSession } from '@/shared/api'
import { registerReset } from '@/shared/lib/reset-registry'

import { type TokenSet } from '../api/token-api'

/**
 * Where the two tokens live, and why they live apart.
 *
 * The access token stays in memory only — `authSession` is what the request
 * interceptor reads, and nothing writes it to disk.
 *
 * The refresh token goes to `sessionStorage`, a deliberate departure recorded
 * in CLAUDE.md. Without it every reload would be a fresh sign-in. It is
 * tab-scoped and dies with the tab, and it is never `localStorage`, which
 * survives a browser restart.
 */
const REFRESH_TOKEN_KEY = 'auth-refresh-token'

/**
 * Read from the token response once and kept for display; `/me` carries no
 * name, and the JWT carries no name either — `user` on the token response is
 * the only place it appears.
 */
let displayName: string | null = null

/**
 * The identifier the password grant accepts, kept because re-authentication
 * needs it: the sudo dialog asks only for a password, and goauth still wants
 * something to look the account up by.
 */
let identifier: string | null = null

/**
 * goauth reports this on both grants, so it survives a reload through the
 * refresh exchange. There is no server-side gate behind it — nothing returns
 * `ErrMustChangePassword` — so the client is what makes the flag mean anything.
 */
let mustChangePassword = false

export const getRefreshToken = (): string | null =>
  globalThis.sessionStorage?.getItem(REFRESH_TOKEN_KEY) ?? null

export function storeCredential(tokens: TokenSet): void {
  authSession.setToken(tokens.access_token)

  // Rotation: every exchange returns a fresh refresh token, and the presented
  // one is spent. Keeping the old value would strand the session on reload.
  globalThis.sessionStorage?.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)

  // `/auth/password` answers without `user`, so a password change must not
  // blank a name the sign-in already established.
  if (tokens.user !== undefined) {
    displayName = tokens.user.username ?? tokens.user.email ?? null
    identifier = displayName
  }

  // Absent on the password-change reply, and that reply is exactly the one that
  // clears the flag — so an omitted field means "no longer required".
  mustChangePassword = tokens.must_change_password ?? false
}

export function clearCredential(): void {
  authSession.setToken(null)
  globalThis.sessionStorage?.removeItem(REFRESH_TOKEN_KEY)
  displayName = null
  identifier = null
  mustChangePassword = false
}

export const getDisplayName = (): string | null => displayName

export const getIdentifier = (): string | null => identifier

export const getMustChangePassword = (): boolean => mustChangePassword

// Sign-out and a lost session both run the resets; neither may leave a
// credential behind for the next visitor to the tab.
registerReset(clearCredential)
